import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEOSchema from '../components/SEOSchema';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProgramFramework from '../components/ProgramFramework';
import { 
  Heart, 
  Baby, 
  CheckCircle, 
  ArrowRight, 
  Sparkles,
  Shield,
  Calendar,
  Users,
  ChevronDown,
  Stethoscope,
  Apple,
  Activity
} from 'lucide-react';

const FertilityNutritionPage = () => {
  const [openFaq, setOpenFaq] = React.useState(null);

  const pillars = [
    {
      icon: <Stethoscope className="w-6 h-6" />,
      title: "Hormonal Balance",
      description: "Nutrition strategies targeting hormone regulation, including estrogen, progesterone, and thyroid hormones that influence fertility."
    },
    {
      icon: <Activity className="w-6 h-6" />,
      title: "Metabolic Health",
      description: "Optimizing insulin sensitivity, managing PMOS symptoms, and supporting healthy ovulation through targeted dietary approaches."
    },
    {
      icon: <Apple className="w-6 h-6" />,
      title: "Nutrient Optimization",
      description: "Ensuring adequate intake of fertility-critical nutrients including folate, zinc, omega-3s, vitamin D, and antioxidants."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Inflammation Reduction",
      description: "Anti-inflammatory nutrition to create an optimal environment for conception and early pregnancy."
    }
  ];

  const whoWeHelp = [
    {
      title: "Women with PMOS (previously called PCOS)",
      description: "Specialized nutrition for managing polycystic ovary syndrome and improving ovulation"
    },
    {
      title: "Couples Trying to Conceive",
      description: "Comprehensive plans for both partners to optimize fertility potential"
    },
    {
      title: "Women with Irregular Cycles",
      description: "Dietary strategies to support hormonal balance and cycle regulation"
    },
    {
      title: "Those Preparing for IVF",
      description: "Nutrition support before, during, and after assisted reproduction procedures"
    },
    {
      title: "Women with Thyroid Conditions",
      description: "Thyroid-supportive nutrition that impacts fertility and conception"
    },
    {
      title: "Couples with Unexplained Difficulty Conceiving",
      description: "Comprehensive lifestyle and nutrition optimization when no specific cause is identified"
    }
  ];

  const programFeatures = [
    "Comprehensive fertility health assessment",
    "Personalized meal plans targeting reproductive health",
    "Ovulation tracking and cycle monitoring guidance",
    "Monthly consultations with fertility specialist",
    "Male fertility nutrition support (couple plans)",
    "Supplement recommendations and timing",
    "Stress management and lifestyle guidance",
    "Continuous progress monitoring",
    "Pre-conception preparation plans",
    "IVF nutritional support protocols"
  ];

  const faqs = [
    {
      question: "What is fertility nutrition and how does it help conception?",
      answer: "Fertility nutrition is a specialized dietary approach designed to optimize reproductive health and improve the chances of conception. It works by balancing hormones, reducing inflammation, supporting egg and sperm quality, and creating an optimal internal environment for conception. Our programs focus on nutrient-dense foods that support ovulation, implantation, and early pregnancy, while eliminating dietary factors that may negatively impact fertility."
    },
    {
      question: "Can diet really improve fertility?",
      answer: "Research consistently shows that nutrition plays a significant role in fertility for both men and women. Studies have linked certain dietary patterns to improved ovulation, better sperm quality, and higher success rates in assisted reproduction. While nutrition alone cannot overcome all fertility challenges, optimizing your diet can improve hormonal balance, egg quality, uterine health, and overall reproductive function, making it a valuable component of any fertility journey."
    },
    {
      question: "How is your fertility program different from general nutrition advice?",
      answer: "Our fertility nutrition program is supervised by a reproductive medicine specialist who understands the complex interplay between nutrition and fertility. Unlike generic advice, we assess your complete hormonal profile, consider any underlying conditions like PMOS or thyroid disorders, and create highly targeted plans. We also coordinate with your fertility doctor or clinic to ensure our recommendations complement your medical treatment."
    },
    {
      question: "Do you offer nutrition support for men as well?",
      answer: "Yes, we recognize that male fertility is equally important in conception. Our couple plans include comprehensive nutrition support for male partners, focusing on sperm quality, count, and motility. We address factors like oxidative stress, zinc and selenium intake, and lifestyle modifications that can significantly impact male reproductive health."
    },
    {
      question: "Can your program help with PMOS-related fertility issues?",
      answer: "Absolutely. PMOS is one of our areas of specialty. Our fertility nutrition plans for women with PMOS focus on insulin management, hormone regulation, and supporting ovulation. We use evidence-based dietary strategies that have been shown to improve PMOS symptoms and increase the chances of natural conception or improve outcomes with fertility treatments."
    },
    {
      question: "How long does it take to see results from fertility nutrition?",
      answer: "Fertility nutrition typically requires 3-6 months to show optimal results, as this is the time needed for dietary changes to influence egg quality and hormonal balance. However, some improvements like better cycle regularity and reduced PMOS symptoms may be noticed within 4-8 weeks. We recommend our 25-week or 52-week programs for comprehensive fertility support, allowing sufficient time for meaningful changes."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Fertility Nutrition &amp; Preconception Support | Doctor-Led | TCUBE FITNESS</title>
        <meta name="description" content="Specialized fertility nutrition programs supervised by fertility specialists. PMOS diet plans, IVF nutrition support, and evidence-based dietary strategies to support conception." />
        <meta name="keywords" content="fertility diet plan, fertility nutrition, PMOS diet for fertility, conception diet, IVF nutrition, fertility food, reproductive health nutrition, fertility specialist diet, fertility meal plan, conception nutrition support" />
        <link rel="canonical" href="https://tcubefitness.com/fertility-nutrition" />
        <meta property="og:title" content="Fertility Nutrition Program | Doctor-Supervised Fertility Diet | TCUBE FITNESS" />
        <meta property="og:description" content="Expert fertility nutrition supervised by specialists. Support your conception journey with evidence-based dietary strategies." />
        <meta property="og:url" content="https://tcubefitness.com/fertility-nutrition" />
        <meta property="og:type" content="website" />
      </Helmet>

      <SEOSchema
        type="service"
        name="Fertility Nutrition Program"
        description="Specialized fertility nutrition guidance supervised by qualified specialists, including PMOS-aware diet planning, conception nutrition and IVF support."
        url="/fertility-nutrition"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Fertility Nutrition', path: '/fertility-nutrition' },
        ]}
      />

      <div className="min-h-screen bg-slate-950">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-28 pb-20 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-600/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-rose-600/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500/10 border border-pink-500/30 rounded-full text-pink-400 text-sm font-medium mb-6">
                <Heart className="w-4 h-4" />
                Hope and Harmony Fertility Program
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Fertility Nutrition for Your Conception Journey
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                Nurture your fertility with specialized nutrition designed by reproductive health experts. Our evidence-based programs support hormonal balance, optimize reproductive health, and help create the best conditions for conception.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                <Link
                  to="/submit-details"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-pink-600 to-rose-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-lg shadow-pink-500/25"
                  data-testid="fertility-cta"
                >
                  Start Your Fertility Journey
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a 
                  href="https://wa.me/919515901777"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-slate-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-slate-800"
                >
                  Free Consultation
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-pink-400 font-bold text-lg">Fertility</p>
                  <p className="text-slate-500 text-sm">Specialist Led</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-emerald-400 font-bold text-lg">PMOS</p>
                  <p className="text-slate-500 text-sm">Specialized Care</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-blue-400 font-bold text-lg">Couple</p>
                  <p className="text-slate-500 text-sm">Plans Available</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-orange-400 font-bold text-lg">IVF</p>
                  <p className="text-slate-500 text-sm">Support Ready</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 text-center">
                Understanding <span className="text-pink-400">Fertility Nutrition</span>
              </h2>
              
              <div className="bg-slate-900/50 rounded-3xl p-8 border border-slate-800 mb-12">
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  The journey to parenthood can be challenging, and nutrition plays a more significant role than many realize. Research shows that what you eat can influence hormone levels, egg quality, sperm health, and the uterine environment, all critical factors for successful conception.
                </p>
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  Our Hope and Harmony Fertility Program takes a comprehensive approach to reproductive health nutrition. Supervised by our <Link to="/#team" className="text-pink-400 hover:underline">in-house fertility specialist</Link> and <Link to="/doctor-led-fitness" className="text-blue-400 hover:underline">medical team</Link>, we create personalized plans that address your unique fertility challenges, whether you are dealing with PMOS, irregular cycles, difficulty conceiving, or preparing for assisted reproduction.
                </p>
                <p className="text-slate-300 text-lg leading-relaxed">
                  Unlike generic fertility advice found online, our programs are built on your individual health assessment, hormonal profile, and specific circumstances. We work alongside your fertility clinic or healthcare provider to ensure our nutritional approach complements your medical care.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Four Pillars Section */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Four Pillars of <span className="text-pink-400">Fertility Nutrition</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Our evidence-based approach targets the key factors that influence reproductive health
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {pillars.map((pillar, idx) => (
                <div 
                  key={idx}
                  className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800 hover:border-pink-500/30 transition-all"
                >
                  <div className="w-12 h-12 bg-pink-500/10 rounded-xl flex items-center justify-center text-pink-400 mb-4">
                    {pillar.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{pillar.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{pillar.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who We Help Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Who Our Fertility Program <span className="text-emerald-400">Helps</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Our specialized nutrition support is designed for various fertility journeys
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {whoWeHelp.map((item, idx) => (
                <div 
                  key={idx}
                  className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800 hover:border-emerald-500/30 transition-all"
                >
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Program Features Section */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  What is Included in Our <span className="text-pink-400">Fertility Program</span>
                </h2>
                <p className="text-slate-400">
                  Comprehensive support for your fertility journey
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {programFeatures.map((feature, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-3 bg-slate-900/50 rounded-xl p-4 border border-slate-800"
                  >
                    <CheckCircle className="w-5 h-5 text-pink-400 flex-shrink-0" />
                    <span className="text-slate-300">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="mt-12 text-center">
                <Link 
                  to="/packages#fertility-package"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-600 to-rose-600 text-white px-8 py-4 rounded-full font-bold transition-all hover:scale-105 shadow-lg shadow-pink-500/25"
                >
                  View Fertility Package Details
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Male Fertility Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-r from-blue-900/30 via-indigo-900/30 to-blue-900/30 rounded-3xl p-8 border border-blue-500/20">
                <div className="flex items-center gap-3 mb-6">
                  <Users className="w-8 h-8 text-blue-400" />
                  <h2 className="text-2xl sm:text-3xl font-bold text-white">
                    Male Fertility Support
                  </h2>
                </div>
                
                <p className="text-slate-300 text-lg mb-6">
                  Fertility is a partnership. Our couple plans include comprehensive nutrition support for male partners, recognizing that male factors contribute to approximately 40-50% of fertility challenges.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                    <h4 className="text-white font-semibold mb-2">Sperm Quality Optimization</h4>
                    <p className="text-slate-400 text-sm">Nutrients targeting sperm count, motility, and morphology</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                    <h4 className="text-white font-semibold mb-2">Antioxidant Support</h4>
                    <p className="text-slate-400 text-sm">Reducing oxidative stress that damages sperm DNA</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                    <h4 className="text-white font-semibold mb-2">Hormone Balance</h4>
                    <p className="text-slate-400 text-sm">Supporting testosterone and reproductive hormone health</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                    <h4 className="text-white font-semibold mb-2">Lifestyle Guidance</h4>
                    <p className="text-slate-400 text-sm">Addressing factors that impact male fertility</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Fertility Nutrition <span className="text-pink-400">FAQs</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Common questions about our fertility support programs
              </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, idx) => (
                <div 
                  key={idx}
                  className="bg-slate-900/50 rounded-2xl border border-slate-800 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left"
                  >
                    <span className="text-white font-semibold pr-4">{faq.question}</span>
                    <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${openFaq === idx ? 'rotate-180 text-pink-400' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all ${openFaq === idx ? 'max-h-[500px]' : 'max-h-0'}`}>
                    <p className="px-5 pb-5 text-slate-400 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-pink-900/50 via-rose-900/50 to-pink-900/50"></div>
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <Sparkles className="w-12 h-12 text-pink-400 mx-auto mb-6" />
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Begin Your Path to Parenthood
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                Let our fertility specialists guide your nutrition journey toward conception.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/submit-details"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-slate-100"
                >
                  Book Free Consultation
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/packages#fertility-package"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-white/10"
                >
                  View Fertility Package
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Internal Links Section */}
        <section className="py-12 border-t border-slate-800">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-lg font-semibold text-white mb-4">Related Programs</h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/pregnancy-nutrition" className="text-slate-400 hover:text-emerald-400 transition-colors">Pregnancy Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/doctor-led-fitness" className="text-slate-400 hover:text-blue-400 transition-colors">Doctor-Led Fitness</Link>
                <span className="text-slate-700">|</span>
                <Link to="/medical-nutrition-therapy" className="text-slate-400 hover:text-emerald-400 transition-colors">Clinical Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/our-team" className="text-slate-400 hover:text-orange-400 transition-colors">Meet Our Team</Link>
                <span className="text-slate-700">|</span>
                <Link to="/packages" className="text-slate-400 hover:text-cyan-400 transition-colors">All Programs</Link>
              </div>
            </div>
          </div>
        </section>

        <ProgramFramework slug="fertility-nutrition" />
        <Footer />
      </div>
    </>
  );
};

export default FertilityNutritionPage;
